import { useState, type FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth.js';
import {
  isPendingComment,
  useAddComment,
  useComments,
  useDeleteComment,
  type PendingComment,
} from '../../api/invoices.js';
import { MessageSquare } from 'lucide-react';
import type { Comment } from '../../api/types.js';
import { formatRelative } from './format.js';
import { EmptyState } from '../../components/EmptyState.js';

const MAX_LENGTH = 4000;

interface Props {
  primaryKey: string;
}

export function CommentsThread({ primaryKey }: Props) {
  const { user } = useAuth();
  const query = useComments(primaryKey);
  const addMutation = useAddComment(primaryKey);
  const deleteMutation = useDeleteComment(primaryKey);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const trimmed = draft.trimEnd();
  const length = trimmed.length;
  const disabled = length === 0 || length > MAX_LENGTH || addMutation.isPending;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (disabled || !user) return;
    setError(null);
    const optimistic: PendingComment = {
      commentId: `pending-${Date.now()}`,
      primaryKey,
      tenantId: '',
      authorUid: user.uid,
      authorEmail: user.email ?? null,
      authorDisplayName: user.displayName ?? null,
      body: trimmed,
      createdAt: new Date().toISOString(),
      pending: true,
    };
    try {
      await addMutation.mutateAsync({ body: trimmed, optimistic });
      setDraft('');
    } catch {
      setError("Échec de l'envoi. Réessayez.");
    }
  }

  async function handleDelete(commentId: string) {
    setError(null);
    // eslint-disable-next-line no-alert
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await deleteMutation.mutateAsync(commentId);
    } catch {
      setError('Suppression refusée.');
    }
  }

  const items = [...(query.data ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="comments-block" data-testid="comments-block">
      {query.isLoading ? (
        <div data-testid="comments-loading">
          <div className="comments-skeleton" />
          <div className="comments-skeleton" />
        </div>
      ) : query.isError ? (
        <div className="comments__error" role="alert" data-testid="comments-fetch-error">
          Impossible de charger les commentaires.
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          testId="comments-empty"
          variant="inline"
          icon={<MessageSquare aria-hidden="true" size={18} strokeWidth={1.5} />}
          title="Aucun commentaire interne"
          description="Ajoutez une note privée pour votre équipe."
        />
      ) : (
        <ul className="comments" data-testid="comments-list">
          {items.map((c) => (
            <CommentItem
              key={c.commentId}
              comment={c}
              canDelete={Boolean(user && c.authorUid === user.uid && !isPendingComment(c))}
              onDelete={handleDelete}
            />
          ))}
        </ul>
      )}

      <form
        onSubmit={handleSubmit}
        className="comments-composer"
        data-testid="comments-composer"
        aria-label="Composer"
      >
        <textarea
          className="comments-composer__textarea"
          placeholder="Ajouter un commentaire interne…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={MAX_LENGTH + 100}
          rows={2}
          data-testid="comments-composer-textarea"
        />
        <div className="comments-composer__row">
          <span className="muted comments-composer__counter" aria-live="polite">
            {length}/{MAX_LENGTH}
          </span>
          <button
            type="submit"
            className="btn-primary"
            disabled={disabled}
            data-testid="comments-composer-submit"
          >
            {addMutation.isPending ? 'Envoi…' : 'Publier'}
          </button>
        </div>
        {error ? (
          <div className="comments-composer__error" role="alert">
            {error}
          </div>
        ) : null}
      </form>
    </div>
  );
}

function CommentItem({
  comment,
  canDelete,
  onDelete,
}: {
  comment: Comment;
  canDelete: boolean;
  onDelete: (id: string) => void;
}) {
  const pending = isPendingComment(comment);
  return (
    <li
      className={`comment${pending ? ' comment--pending' : ''}`}
      data-testid={`comment-${comment.commentId}`}
    >
      <div className="comment__head">
        <span className="comment__author">
          {comment.authorDisplayName ?? comment.authorEmail ?? comment.authorUid}
        </span>
        {comment.authorEmail && comment.authorDisplayName ? (
          <span className="comment__email muted">{comment.authorEmail}</span>
        ) : null}
        <time
          className="comment__time muted"
          dateTime={comment.createdAt}
          title={comment.createdAt}
        >
          {pending ? 'Envoi…' : formatRelative(comment.createdAt)}
        </time>
        {canDelete ? (
          <button
            type="button"
            className="comment__delete"
            onClick={() => onDelete(comment.commentId)}
            aria-label="Supprimer ce commentaire"
            data-testid={`comment-delete-${comment.commentId}`}
          >
            ×
          </button>
        ) : null}
      </div>
      <div className="comment__body">{comment.body}</div>
    </li>
  );
}
